import styled from '@emotion/styled';
import { css, keyframes } from '@emotion/react';
import { forwardRef } from 'react';
import type { LinearLoadingProps, LinearLoadingVariant, LinearLoadingColor, LinearLoadingSize } from './LinearLoading.types';

const indeterminateAnimation = keyframes`
  0% {
    left: -40%;
    right: 100%;
  }
  60% {
    left: 100%;
    right: -90%;
  }
  100% {
    left: 100%;
    right: -90%;
  }
`;

const indeterminateAnimation2 = keyframes`
  0% {
    left: -200%;
    right: 100%;
  }
  60% {
    left: 107%;
    right: -8%;
  }
  100% {
    left: 107%;
    right: -8%;
  }
`;

const bufferAnimation = keyframes`
  0% {
    opacity: 1;
    background-position: 0 -23px;
  }
  60% {
    opacity: 0;
    background-position: 0 -23px;
  }
  100% {
    opacity: 1;
    background-position: -200px -23px;
  }
`;

const getColorValue = (color: LinearLoadingColor) => {
  switch (color) {
    case 'success':
      return 'var(--ib-color-green-500, #22c55e)';
    case 'error':
      return 'var(--ib-color-red-500, #ef4444)';
    case 'warning':
      return 'var(--ib-color-orange-500, #f97316)';
    case 'info':
      return 'var(--ib-color-blue-400, #60a5fa)';
    case 'primary':
    default:
      return 'var(--color-highlight, #F4770B)';
  }
};

const getBackgroundColor = (color: LinearLoadingColor) => {
  switch (color) {
    case 'success':
      return 'var(--ib-color-green-100, #dcfce7)';
    case 'error':
      return 'var(--ib-color-red-100, #fee2e2)';
    case 'warning':
      return 'var(--ib-color-orange-100, #ffedd5)';
    case 'info':
      return 'var(--ib-color-blue-100, #dbeafe)';
    case 'primary':
    default:
      return 'var(--ib-color-orange-100, #FFEEDD)';
  }
};

const getSizeStyles = (size: LinearLoadingSize) => {
  switch (size) {
    case 'small':
      return css`
        height: 4px;
      `;
    case 'large':
      return css`
        height: 12px;
      `;
    case 'medium':
    default:
      return css`
        height: 8px;
      `;
  }
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

interface ProgressContainerProps {
  size: LinearLoadingSize;
  bgColor: string;
}

const ProgressContainer = styled.div<ProgressContainerProps>`
  position: relative;
  overflow: hidden;
  border-radius: 0;
  width: 100%;
  background-color: ${props => props.bgColor};
  
  ${props => getSizeStyles(props.size)}
`;

interface ProgressBarProps {
  variant: LinearLoadingVariant;
  progressColor: string;
  value: number;
}

const ProgressBar = styled.div<ProgressBarProps>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  background-color: ${props => props.progressColor};
  border-radius: 0;
  transition: width 0.3s ease;
  
  ${props => props.variant === 'determinate' 
    ? css`
        width: ${Math.min(100, Math.max(0, props.value))}%;
      `
    : css`
        width: 50%;
        animation: ${indeterminateAnimation} 2.1s cubic-bezier(0.65, 0.815, 0.735, 0.395) infinite;
      `
  }
`;

interface SecondaryBarProps {
  progressColor: string;
}

const SecondaryBar = styled.div<SecondaryBarProps>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 50%;
  background-color: ${props => props.progressColor};
  border-radius: 0;
  opacity: 0.4;
  animation: ${indeterminateAnimation2} 2.1s cubic-bezier(0.165, 0.84, 0.44, 1) 1.15s infinite;
`;

interface BufferBarProps {
  bgColor: string;
  bufferValue: number;
}

const BufferBar = styled.div<BufferBarProps>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: ${props => props.bufferValue}%;
  background-color: ${props => props.bgColor};
  border-radius: 0;
  animation: ${bufferAnimation} 3s infinite linear;
`;

const LabelContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LabelText = styled.span`
  font-size: 12px;
  color: var(--ib-color-grey-600, #4b5563);
`;

const PercentageText = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: var(--ib-color-grey-700, #374151);
`;

export const LinearLoading = forwardRef<HTMLDivElement, LinearLoadingProps>(
  (
    {
      value = 0,
      variant = 'indeterminate',
      color = 'primary',
      size = 'medium',
      showLabel = false,
      label,
      buffer,
      className,
      'data-testid': testId,
      dataTestId,
      dataId,
    },
    ref
  ) => {
    const progressColor = getColorValue(color);
    const bgColor = getBackgroundColor(color);
    const clampedValue = Math.min(100, Math.max(0, value));
    const hasBuffer = buffer !== undefined && variant === 'determinate';

    return (
      <Container ref={ref} className={className} data-testid={dataTestId || testId} data-id={dataId}>
        {showLabel && (
          <LabelContainer>
            <LabelText>{label}</LabelText>
            {variant === 'determinate' && (
              <PercentageText>{Math.round(clampedValue)}%</PercentageText>
            )}
          </LabelContainer>
        )}
        <ProgressContainer
          size={size}
          bgColor={bgColor}
          role="progressbar"
          aria-valuenow={variant === 'determinate' ? clampedValue : undefined}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          {hasBuffer && (
            <BufferBar bgColor={bgColor} bufferValue={Math.min(100, Math.max(0, buffer))} />
          )}
          <ProgressBar
            variant={variant}
            progressColor={progressColor}
            value={clampedValue}
          />
          {variant === 'indeterminate' && (
            <SecondaryBar progressColor={progressColor} />
          )}
        </ProgressContainer>
      </Container>
    );
  }
);

LinearLoading.displayName = 'LinearLoading';
